// Preload critical resources for better performance and SEO

export class ResourcePreloader {
  private static instance: ResourcePreloader
  private preloadedImages: Set<string> = new Set()
  
  static getInstance(): ResourcePreloader {
    if (!ResourcePreloader.instance) {
      ResourcePreloader.instance = new ResourcePreloader()
    }
    return ResourcePreloader.instance
  }

  // Preload critical images
  preloadImage(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.preloadedImages.has(src)) {
        resolve()
        return
      }

      const img = new Image()
      img.onload = () => {
        this.preloadedImages.add(src)
        resolve()
      }
      img.onerror = reject
      img.src = src
    })
  }

  // Preload multiple images
  preloadImages(srcs: string[]): Promise<void[]> {
    return Promise.all(srcs.map(src => this.preloadImage(src)))
  }

  // Preload critical fonts
  preloadFont(fontUrl: string, fontFormat: string = 'woff2'): void {
    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'font'
    link.type = `font/${fontFormat}`
    link.crossOrigin = 'anonymous'
    link.href = fontUrl
    document.head.appendChild(link)
  }

  // Preload CSS
  preloadCSS(cssUrl: string): void {
    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'style'
    link.href = cssUrl
    link.onload = () => {
      link.rel = 'stylesheet'
    }
    document.head.appendChild(link)
  }

  // Prefetch next likely page
  prefetchRoute(routeUrl: string): void {
    const link = document.createElement('link')
    link.rel = 'prefetch'
    link.href = routeUrl
    document.head.appendChild(link)
  }

  // DNS prefetch for external resources
  dnsPrefetch(domain: string): void {
    const link = document.createElement('link')
    link.rel = 'dns-prefetch'
    link.href = domain
    document.head.appendChild(link)
  }

  // Preconnect to critical external resources
  preconnect(domain: string): void {
    const link = document.createElement('link')
    link.rel = 'preconnect'
    link.href = domain
    link.crossOrigin = 'anonymous'
    document.head.appendChild(link)
  }
}

// Initialize critical preloads
export function initCriticalPreloads() {
  const preloader = ResourcePreloader.getInstance()
  
  // DNS prefetch for external services
  preloader.dnsPrefetch('//fonts.googleapis.com')
  preloader.dnsPrefetch('//api.safaricom.co.ke') // M-Pesa API
  
  // Preconnect to critical services
  preloader.preconnect('https://fonts.gstatic.com')
  
  // Preload critical images
  const criticalImages = [
    '/og-image.svg',
    '/twitter-image.svg',
    '/favicon.ico'
  ]
  
  preloader.preloadImages(criticalImages).catch(console.warn)
}

// Auto-prefetch likely next pages based on current route
export function setupIntelligentPrefetching() {
  const preloader = ResourcePreloader.getInstance()
  
  const prefetchMap: Record<string, string[]> = {
    '/': ['/catalog', '/login', '/about'],
    '/catalog': ['/login', '/register', '/'],
    '/login': ['/dashboard', '/pos', '/catalog'],
    '/register': ['/login', '/catalog'],
    '/dashboard': ['/pos', '/inventory', '/orders'],
    '/pos': ['/dashboard', '/inventory'],
    '/inventory': ['/products', '/categories', '/dashboard'],
    '/products': ['/categories', '/inventory'],
    '/orders': ['/dashboard', '/pos']
  }
  
  const currentPath = window.location.pathname
  const nextPaths = prefetchMap[currentPath] || []
  
  // Delay prefetching to avoid interfering with current page load
  setTimeout(() => {
    nextPaths.forEach(path => preloader.prefetchRoute(path))
  }, 2000)
}

// Performance monitoring
export function addPerformanceObserver() {
  if ('PerformanceObserver' in window) {
    // Observe Largest Contentful Paint (LCP)
    const lcpObserver = new PerformanceObserver((list) => {
      // Track LCP for performance monitoring (logging removed)
    })
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true })

    // Observe First Input Delay (FID)
    const fidObserver = new PerformanceObserver((list) => {
      // Track FID for performance monitoring (logging removed)
    })
    fidObserver.observe({ type: 'first-input', buffered: true })

    // Observe Cumulative Layout Shift (CLS)
    const clsObserver = new PerformanceObserver((list) => {
      // Track CLS for performance monitoring (logging removed)
    })
    clsObserver.observe({ type: 'layout-shift', buffered: true })
  }
}